import { useEffect, useState } from 'react'
import { Menu, ArrowDownRight, Maximize2, PanelLeftClose, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InstallAgentButton } from '@/components/voice/InstallAgentButton'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useAssistantRuntime } from '@/lib/contexts/AssistantRuntimeContext'
import {
  readPhoneDialerLaunchMode,
  writePhoneDialerLaunchMode,
  type PhoneDialerLaunchMode,
} from '@/lib/voice-console/client-tools'
import { useConsoleState } from '@/lib/voice-console/state/context'
import type { AssistantStatus } from '@/lib/voice-console/state/types'
import { cn } from '@/lib/utils'

function statusColor(status: AssistantStatus): string {
  switch (status) {
    case 'disconnected': return 'bg-slate-600'
    case 'connecting': return 'bg-amber-400 animate-pulse'
    case 'idle': return 'bg-cyan-500'
    case 'listening': return 'bg-cyan-400'
    case 'processing': return 'bg-indigo-400 animate-pulse'
    case 'tool_use': return 'bg-violet-400 animate-pulse'
    case 'speaking': return 'bg-pink-400'
    case 'error': return 'bg-red-500'
    default: return 'bg-slate-600'
  }
}

function statusLabel(status: AssistantStatus): string {
  switch (status) {
    case 'disconnected': return 'Offline'
    case 'connecting': return 'Connecting…'
    case 'idle': return 'Ready'
    case 'listening': return 'Listening'
    case 'processing': return 'Processing'
    case 'tool_use': return 'Working'
    case 'speaking': return 'Speaking'
    case 'error': return 'Error'
    default: return status
  }
}

type AutoStartMode = 'voice' | 'chat' | 'none'

function readAutoStart(agentId: string): AutoStartMode {
  if (!agentId) return 'voice'
  const v = localStorage.getItem(`botnexus:autostart:${agentId}`)
  if (v === 'voice' || v === 'chat' || v === 'none') return v as AutoStartMode
  return 'voice'
}

function writeAutoStart(agentId: string, mode: AutoStartMode) {
  localStorage.setItem(`botnexus:autostart:${agentId}`, mode)
}

interface StatusBarProps {
  agentName?: string
  agentId?: string
}

export function StatusBar({ agentName, agentId }: StatusBarProps) {
  const { state } = useConsoleState()
  const { mode, minimizeToWidget, restoreFullscreen, endSession } = useAssistantRuntime()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [autoStart, setAutoStart] = useState<AutoStartMode>(() =>
    readAutoStart(agentId ?? '')
  )
  const [dialerLaunchMode, setDialerLaunchMode] = useState<PhoneDialerLaunchMode>(() =>
    readPhoneDialerLaunchMode()
  )
  const { assistantStatus, ablyConnectionState, session } = state
  const name = session?.agentName ?? agentName ?? 'Assistant'

  useEffect(() => {
    setAutoStart(readAutoStart(agentId ?? ''))
  }, [agentId])

  const handleAutoStartChange = (mode: AutoStartMode) => {
    setAutoStart(mode)
    if (agentId) writeAutoStart(agentId, mode)
  }

  const handleDialerLaunchModeChange = (mode: PhoneDialerLaunchMode) => {
    setDialerLaunchMode(mode)
    writePhoneDialerLaunchMode(mode)
  }

  const handleSurfaceAction = () => {
    setIsSettingsOpen(false)
    if (mode === 'fullscreen') {
      minimizeToWidget()
    } else if (mode === 'widget') {
      restoreFullscreen()
    }
  }

  const handleEndSession = () => {
    setIsSettingsOpen(false)
    endSession()
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm shrink-0">
        <div className="flex items-center min-w-0 gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
            onClick={(event) => {
              event.currentTarget.blur()
              setIsSettingsOpen((open) => !open)
            }}
            aria-label={isSettingsOpen ? 'Close settings panel' : 'Open settings panel'}
            aria-expanded={isSettingsOpen}
            aria-controls="assistant-console-settings-panel"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold text-slate-200 truncate">{name}</span>
        </div>

        {/* Status indicators */}
        <div className="flex items-center gap-2">
          <span className={cn('w-2 h-2 rounded-full shrink-0', statusColor(assistantStatus))} />
          <span className="text-xs text-slate-400">{statusLabel(assistantStatus)}</span>
          {/* Ably realtime dot — secondary indicator */}
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full shrink-0 ml-1',
              ablyConnectionState === 'connected' ? 'bg-green-500' :
              ablyConnectionState === 'connecting' ? 'bg-amber-400 animate-pulse' :
              'bg-slate-700'
            )}
            title={`Realtime: ${ablyConnectionState}`}
          />
        </div>
      </div>
      <Sheet open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <SheetContent
          id="assistant-console-settings-panel"
          side="left"
          className="w-80 max-w-[90vw] border-slate-800 bg-slate-950 text-slate-100"
        >
          <SheetHeader className="space-y-1">
            <SheetTitle className="text-slate-100">Settings</SheetTitle>
            <SheetDescription className="text-slate-400">
              Console actions
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              className="justify-start gap-2 border border-slate-800 bg-slate-900/60 text-slate-100 hover:bg-slate-800 hover:text-white"
              onClick={handleSurfaceAction}
            >
              {mode === 'fullscreen' ? <ArrowDownRight className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              {mode === 'fullscreen' ? 'Minimize' : 'Restore Fullscreen'}
            </Button>

            <InstallAgentButton
              fullLabel
              className="w-full justify-start border-slate-800 bg-slate-900/60 text-slate-100 hover:bg-slate-800 hover:text-white"
            />

            {/* Auto-start preference */}
            <div className='flex flex-col gap-2'>
              <span className='text-xs text-slate-400 font-medium uppercase tracking-wide'>
                Auto-start session
              </span>
              <div className='flex gap-1 rounded-lg bg-slate-900 border border-slate-800 p-1'>
                {(['voice', 'chat', 'none'] as AutoStartMode[]).map((mode) => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => handleAutoStartChange(mode)}
                    className={cn(
                      'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                      autoStart === mode
                        ? mode === 'voice'
                          ? 'bg-cyan-600 text-white'
                          : mode === 'chat'
                            ? 'bg-emerald-700 text-white'
                            : 'bg-slate-700 text-slate-200'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                    )}
                  >
                    {mode === 'voice' ? '🎤 Voice' : mode === 'chat' ? '💬 Text' : '— Off'}
                  </button>
                ))}
	              </div>
	            </div>

	            <div className="flex flex-col gap-2">
	              <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
	                Dialer launch
	              </span>
	              <div className="grid gap-2">
	                {([
	                  {
	                    mode: 'blank_page',
	                    label: 'Hands-free blank page',
	                    description: 'Default. Tries to open the dialer automatically.',
	                  },
	                  {
	                    mode: 'gesture_prompt',
	                    label: 'Gesture fallback',
	                    description: 'Shows an Open dialer button for stricter browsers.',
	                  },
	                ] satisfies Array<{
	                  mode: PhoneDialerLaunchMode
	                  label: string
	                  description: string
	                }>).map((option) => (
	                  <button
	                    key={option.mode}
	                    type="button"
	                    onClick={() => handleDialerLaunchModeChange(option.mode)}
	                    className={cn(
	                      'rounded-lg border px-3 py-2 text-left transition-colors',
	                      dialerLaunchMode === option.mode
	                        ? 'border-cyan-600 bg-cyan-950/60 text-cyan-100'
	                        : 'border-slate-800 bg-slate-900/60 text-slate-300 hover:border-slate-700 hover:bg-slate-800'
	                    )}
	                  >
	                    <span className="block text-sm font-medium">{option.label}</span>
	                    <span className="mt-1 block text-xs leading-4 text-slate-400">
	                      {option.description}
	                    </span>
	                  </button>
	                ))}
	              </div>
	              <p className="text-xs leading-4 text-slate-500">
	                If a device shows a blank screen, switch to Gesture fallback.
	              </p>
	            </div>

	            <Button
	              type="button"
              variant="ghost"
              className="justify-start gap-2 border border-slate-800 bg-slate-900/60 text-slate-100 hover:bg-slate-800 hover:text-white"
              onClick={handleEndSession}
            >
              <Square className="h-4 w-4" />
              End Session
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="justify-start gap-2 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
              onClick={() => setIsSettingsOpen(false)}
            >
              <PanelLeftClose className="h-4 w-4" />
              Close Settings
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
